import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck,
  Filter,
  Search,
  Download,
  Activity,
  User,
  Building2,
  Clock,
  LogIn,
  UserPlus,
  Sparkles,
  BookOpenCheck,
  Coins,
  Trash2,
  Edit3,
  Landmark,
  ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_META = {
  login: { label: "Login", icon: LogIn, color: "text-sky-300", bg: "bg-sky-500/10", border: "border-sky-500/30" },
  candidato_aprovado: { label: "Candidato aprovado", icon: Sparkles, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  candidato_iniciado: { label: "Candidato iniciado", icon: UserPlus, color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10", border: "border-[#D4AF37]/40" },
  balaustre_aprovado: { label: "Balaústre aprovado", icon: BookOpenCheck, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  balaustre_deleted: { label: "Balaústre excluído", icon: Trash2, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  mensalidade_paga: { label: "Mensalidade paga", icon: Coins, color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  loja_created: { label: "Loja criada", icon: Building2, color: "text-purple-300", bg: "bg-purple-500/10", border: "border-purple-500/30" },
  loja_updated: { label: "Loja atualizada", icon: Edit3, color: "text-purple-300", bg: "bg-purple-500/10", border: "border-purple-500/30" },
  template_created: { label: "Template criado", icon: BookOpenCheck, color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  template_updated: { label: "Template atualizado", icon: Edit3, color: "text-cyan-300", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  template_deleted: { label: "Template excluído", icon: Trash2, color: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/30" },
};

const formatDT = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit"
    });
  } catch { return iso; }
};

export const AuditLogsView = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [eventFilter, setEventFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = eventFilter !== "all" ? `?event=${eventFilter}&limit=300` : "?limit=300";
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/audit-logs${params}`),
        axios.get(`${API}/audit-logs/stats`),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Erro ao carregar auditoria.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line
  }, [eventFilter]);

  const filtered = useMemo(
    () =>
      logs.filter(
        (l) =>
          (l.actor_name || "").toLowerCase().includes(search.toLowerCase()) ||
          (l.event || "").toLowerCase().includes(search.toLowerCase()) ||
          (l.target_id || "").toLowerCase().includes(search.toLowerCase())
      ),
    [logs, search]
  );

  const exportCSV = () => {
    if (!logs.length) return;
    const rows = [
      ["Data/Hora", "Evento", "Alvo", "ID Alvo", "Autor", "Perfil", "Detalhes"],
      ...logs.map((l) => [
        formatDT(l.timestamp),
        l.event,
        l.target_type,
        l.target_id || "",
        l.actor_name,
        l.actor_role,
        JSON.stringify(l.details || {}).replaceAll(",", ";"),
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auditoria_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Auditoria exportada em CSV.");
  };

  return (
    <div className="space-y-6" data-testid="audit-logs-view">
      {/* Header */}
      <div className="luxury-card p-6 border-l-4 border-l-purple-400">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-purple-400 font-bold mb-1">
              <ShieldCheck className="w-4 h-4" />
              <span>Master da Loja • Trilha de Auditoria</span>
            </div>
            <h2 className="text-2xl font-serif font-bold text-white">Registro de Eventos e Aprovações</h2>
            <p className="text-xs text-slate-400 mt-1">
              Histórico imutável de todas as ações relevantes: aprovações, iniciações, pagamentos e alterações administrativas.
            </p>
          </div>
          <button
            data-testid="audit-btn-export-csv"
            onClick={exportCSV}
            className="flex items-center space-x-2 bg-purple-500 hover:bg-purple-600 text-white font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider shadow-[0_0_15px_rgba(168,85,247,0.25)]"
          >
            <Download className="w-4 h-4" />
            <span>Exportar CSV</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="audit-input-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por autor, evento ou ID..."
              className="w-full bg-[#0B111E] border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:border-purple-400 outline-none"
            />
          </div>
          <select
            data-testid="audit-select-event"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="bg-[#0B111E] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-purple-400 outline-none"
          >
            <option value="all">Todos os eventos</option>
            {Object.entries(EVENT_META).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="luxury-card p-5 border border-purple-500/30">
            <div className="text-[10px] uppercase text-slate-400 font-bold">Eventos (todo período)</div>
            <div className="text-3xl font-serif font-bold text-purple-300 mt-1 font-mono">{stats.total_all_time}</div>
          </div>
          <div className="luxury-card p-5 border border-purple-500/30">
            <div className="text-[10px] uppercase text-slate-400 font-bold">Últimos 30 dias</div>
            <div className="text-3xl font-serif font-bold text-[#D4AF37] mt-1 font-mono">{stats.last_30_days}</div>
          </div>
          <div className="luxury-card p-5 border border-purple-500/30">
            <div className="text-[10px] uppercase text-slate-400 font-bold mb-2">Tipos mais frequentes (30d)</div>
            <div className="flex flex-wrap gap-1.5">
              {(stats.by_event || []).slice(0, 4).map((e) => (
                <span key={e.event} className="text-[10px] px-2 py-1 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300">
                  {(EVENT_META[e.event]?.label || e.event)}: <strong className="text-purple-300">{e.count}</strong>
                </span>
              ))}
              {(!stats.by_event || stats.by_event.length === 0) && (
                <span className="text-[10px] text-slate-500 italic">Nenhum evento no período.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="luxury-card p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-serif font-bold text-white flex items-center space-x-2">
            <Activity className="w-4 h-4 text-purple-400" />
            <span>Linha do Tempo — {filtered.length} eventos</span>
          </h3>
        </div>

        <div className="max-h-[65vh] overflow-y-auto divide-y divide-slate-800/60">
          {loading && <div className="p-6 text-center text-slate-500 text-xs">Carregando registros...</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-slate-500 text-xs">
              <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
              Nenhum evento registrado nesse filtro.
              <div className="text-[10px] mt-2 italic">Cada ação relevante (aprovação, pagamento, exclusão) é registrada automaticamente.</div>
            </div>
          )}
          {filtered.map((l) => {
            const meta = EVENT_META[l.event] || { label: l.event, icon: Activity, color: "text-slate-300", bg: "bg-slate-700/40", border: "border-slate-600" };
            const Icon = meta.icon;
            return (
              <div
                key={l.id}
                data-testid={`audit-log-${l.id}`}
                className="px-6 py-3 hover:bg-white/[0.02] flex items-start space-x-4"
              >
                <div className={`shrink-0 w-9 h-9 rounded-xl ${meta.bg} border ${meta.border} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className={`text-[10px] uppercase font-bold tracking-wider ${meta.color}`}>{meta.label}</span>
                    <span className="text-[10px] text-slate-500">•</span>
                    <span className="text-[10px] text-slate-500 font-mono flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatDT(l.timestamp)}</span>
                    </span>
                  </div>
                  <div className="text-sm text-white mt-0.5">
                    <strong>{l.actor_name}</strong>
                    <span className="text-slate-400 text-xs"> ({l.actor_role})</span>
                    {l.target_id && (
                      <span className="text-slate-400 text-xs"> → {l.target_type}:<span className="font-mono ml-1">{l.target_id}</span></span>
                    )}
                  </div>
                  {l.details && Object.keys(l.details).length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 font-mono truncate">
                      {Object.entries(l.details).slice(0, 4).map(([k, v]) => (
                        <span key={k} className="mr-3">
                          <span className="text-slate-400">{k}:</span> {String(v).slice(0, 60)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
